import type { MenuIconComponent } from "@/features/navigation/utils/iconResolver";
import { SidebarMotionLink } from "@/shared/components/SidebarMotionLink";
import {
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/shared/components/ui/sidebar";
import { cn } from "@/shared/utils/style.util";
import type { LucideIcon } from "lucide-react";

type SidebarNavItemProps = {
  to: string
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  isActive: boolean
  icon: MenuIconComponent
  label: string
  warningIcon?: LucideIcon
  hasWarning?: boolean
};

function SidebarNavItem({
  to,
  onClick,
  isActive,
  icon: IconComponent,
  label,
  warningIcon: WarningIconComponent,
  hasWarning,
}: SidebarNavItemProps) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        className={cn(
          "group/link hover:bg-sidebar-primary/10 hover:text-sidebar-primary dark:hover:bg-sidebar-primary/15",
          hasWarning &&
          !isActive &&
          "text-red-600 dark:text-red-500 hover:text-red-700 dark:hover:text-red-400",
        )}
      >
        <SidebarMotionLink
          to={to}
          onClick={onClick}
          disableHover={isCollapsed}
        >
          <IconComponent size={20} className="shrink-0 text-[var(--sidebar-icon-foreground)] group-hover/link:text-sidebar-primary" />
          <span className="truncate" title={label}>{label}</span>
          {hasWarning && WarningIconComponent && (
            <WarningIconComponent size={20} className="flex-shrink-0 ml-auto text-[var(--sidebar-icon-foreground)] group-hover/link:text-sidebar-primary" />
          )}
        </SidebarMotionLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

SidebarNavItem.displayName = "SidebarNavItem";

export { SidebarNavItem };
